import { useEffect, useRef, useState } from "react";
import Iconify from "../Iconify";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { useTranslations } from "@/lib/static-intl";

const LivePhotoPlayer = ({
  photoUrl,
  videoUrl,
  autoplay = false,
  skipLiveBadge = true,
}) => {
  const videoRef = useRef(null);
  const { isAutoplayEnabled, toggleAutoplay } = useAuthContext();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isHoveringBadge, setIsHoveringBadge] = useState(false);
  const t = useTranslations("live_image");

  const effectiveAutoplay = isAutoplayEnabled ?? autoplay;

  useEffect(() => {
    if (effectiveAutoplay && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();

      const timer = setTimeout(() => {
        videoRef.current.pause();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [effectiveAutoplay]);

  const handleBadgeMouseEnter = () => {
    if (videoRef.current && !showDropdown) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
    setIsHoveringBadge(true);
  };

  const handleBadgeMouseLeave = () => {
    if (videoRef.current && !showDropdown) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setIsHoveringBadge(false);
    setTimeout(() => {
      if (!showDropdown) {
        setShowDropdown(false)
      }
    }, 100)
  };

  const handleDropdownClick = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleAutoplayToggle = (e) => {
    e.stopPropagation();
    toggleAutoplay();

    // If turning off autoplay and video is playing, pause it
    if (effectiveAutoplay && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(false);
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  return (
    <div
      className="relative w-full h-full overflow-hidden rounded-xl"
    >
      {/* Static photo fallback */}
      <img
        src={photoUrl}
        alt="Live Preview"
        className="absolute inset-0 w-full h-full object-contain z-0"
        draggable={false}
      />

      {/* Video overlay */}
      <video
        ref={videoRef}
        src={videoUrl}
        muted
        playsInline
        preload={effectiveAutoplay ? "metadata" : "none"}
        className="absolute inset-0 w-full h-full object-contain z-10"
      />

      {/* LIVE badge with dropdown */}
      {!skipLiveBadge && (
        <div className="absolute top-4 left-4 z-20">
          <div
            onMouseEnter={handleBadgeMouseEnter}
            onMouseLeave={handleBadgeMouseLeave}
            className="flex items-center gap-1 backdrop-blur-sm bg-black/20 dark:bg-white/10 text-white text-xs px-2 py-1 rounded-full cursor-pointer relative transition-all duration-200 ease-in-out hover:bg-black/30 dark:hover:bg-white/20"
          >
            <Iconify icon={effectiveAutoplay ? "tabler:live-photo" : "tabler:live-photo-off"} className={"!w-4 !text-white"} />
            <span>LIVE</span>

            {/* Dropdown arrow - shows on hover with smooth transition */}
            {isHoveringBadge && (
              <div
                onClick={handleDropdownClick}
                className="hover:bg-white/20 rounded-full transition-all duration-200 ease-in-out transform animate-in fade-in-0 slide-in-from-left-1 zoom-in-95"
              >
                <Iconify
                  icon={showDropdown ? "mdi:chevron-up" : "mdi:chevron-down"}
                  className="!w-4 !h-4 !text-white transition-transform duration-150 ease-in-out"
                />
              </div>
            )}
          </div>

          {/* Dropdown menu with smooth transition */}
          {showDropdown && (
            <div
              className="absolute top-full left-0 mt-1 backdrop-blur-sm bg-black/20 dark:bg-white/10 text-white hover:bg-black/30 dark:hover:bg-white/20 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-max animate-in fade-in-0 zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={() => setShowDropdown(true)}
              onMouseLeave={() => setShowDropdown(false)}
            >
              <button
                onClick={handleAutoplayToggle}
                className="w-full px-3 py-2 text-left text-sm transition-colors duration-150 flex items-center gap-2"
              >
                <span className="transition-all duration-150 text-xs">
                  {t(effectiveAutoplay ? "autoplay_off" : "autoplay_on")}
                </span>
              </button>
            </div>
          )}
        </div>
      )
      }
    </div >
  );
};

export default LivePhotoPlayer;
